import React from 'react';
import { styled } from '@compiled/react';
import { graphql, useFragment } from 'react-relay';
import { token } from '@atlaskit/tokens';
import { gridSize } from '@atlassian/jira-common-styles/src/main.tsx';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import type { async_softwareReleasesVersionRichTextSection_AsyncVersionRichTextSection$key } from '@atlassian/jira-relay/src/__generated__/async_softwareReleasesVersionRichTextSection_AsyncVersionRichTextSection.graphql';
import { lazy } from '@atlassian/react-loosely-lazy';
import type { VersionRichTextSection } from './index.tsx';

// eslint-disable-next-line jira/deprecations/no-rll-client-async-experiences
const LazyVersionRichTextSection = lazy<typeof VersionRichTextSection>(() =>
	import(/* webpackChunkName: "async-version-rich-text-section" */ './index').then(
		(module) => module.VersionRichTextSection,
	),
);

export const AsyncVersionRichTextSection = ({
	jiraVersion,
}: {
	jiraVersion: async_softwareReleasesVersionRichTextSection_AsyncVersionRichTextSection$key;
}) => {
	const version = useFragment(
		graphql`
			fragment async_softwareReleasesVersionRichTextSection_AsyncVersionRichTextSection on JiraVersion {
				...ui_softwareReleasesVersionRichTextSection_VersionRichTextSection
			}
		`,
		jiraVersion,
	);

	return (
		<Placeholder
			name="rich-text-section"
			fallback={
				<SkeletonWrapper>
					<SkeletonItem width="15%" />
					<SkeletonItem width="45%" />
				</SkeletonWrapper>
			}
		>
			<LazyVersionRichTextSection jiraVersion={version} />
		</Placeholder>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SkeletonItem = styled.div<{ width: string }>({
	backgroundColor: token('color.skeleton'),
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-imported-style-values, @atlaskit/ui-styling-standard/no-unsafe-values -- Ignored via go/DSP-18766
	height: `${gridSize * 1.75}px`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	width: (props) => props.width,
	// eslint-disable-next-line @atlaskit/design-system/no-unsafe-design-token-usage
	borderRadius: token('border.radius.100', '3px'),
	marginTop: token('space.150'),
});

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled
const SkeletonWrapper = styled.div({
	display: 'flex',
	flexDirection: 'column',
	justifyContent: 'flex-start',
	marginTop: token('space.300'),
});
